import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, ExternalLink, Clock, Facebook, Linkedin, Twitter } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { SectionHeader } from '../components/common/SectionHeader';
import { ContactForm } from '../components/contact/ContactForm';
import { GeometricBackground } from '../components/decorations/GeometricBackground';
import { companyInfo } from '../data/company';

const ContactPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-hero-pattern overflow-hidden">
        <GeometricBackground />
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <Badge className="mb-4 bg-primary-foreground/10 text-primary-foreground border-primary-foreground/20">
              Contact Us
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              Get in Touch
            </h1>
            <p className="text-lg text-primary-foreground/80">
              Ready to transform your technology infrastructure? Let&apos;s start a conversation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <Card className="shadow-xl">
                  <CardContent className="p-8">
                    <h2 className="text-2xl font-bold text-foreground mb-2">
                      Send Us a Message
                    </h2>
                    <p className="text-muted-foreground mb-8">
                      Fill out the form below and our team will get back to you within 24-48 hours.
                    </p>
                    <ContactForm />
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            {/* Contact Details - EXACT */}
            <div className="space-y-6">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                {/* Address */}
                <Card className="mb-6">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                        <MapPin className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-2">Address</h3>
                        {companyInfo.contact.address.map((line, i) => (
                          <p key={i} className="text-muted-foreground text-sm">{line}</p>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Phone */}
                <Card className="mb-6">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                        <Phone className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-2">Phone</h3>
                        {companyInfo.contact.phones.map((phone, i) => (
                          <p key={i} className="text-muted-foreground text-sm">{phone}</p>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Email */}
                <Card className="mb-6">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                        <Mail className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-2">Email</h3>
                        <a 
                          href={`mailto:${companyInfo.contact.email}`}
                          className="text-primary hover:underline text-sm"
                        >
                          {companyInfo.contact.email}
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Website */}
                <Card className="mb-6">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                        <ExternalLink className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-2">Website</h3>
                        <a 
                          href={`https://${companyInfo.contact.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline text-sm"
                        >
                          {companyInfo.contact.website}
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Social Media */}
                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-foreground mb-4">Follow Us</h3>
                    <div className="flex gap-3">
                      <a
                        href={`https://facebook.com/${companyInfo.contact.social.facebook}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"
                        aria-label="Facebook"
                      >
                        <Facebook className="w-5 h-5" />
                      </a>
                      <a
                        href={`https://linkedin.com/${companyInfo.contact.social.linkedin}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"
                        aria-label="LinkedIn"
                      >
                        <Linkedin className="w-5 h-5" />
                      </a>
                      <a
                        href={`https://twitter.com/${companyInfo.contact.social.twitter.replace('@', '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"
                        aria-label="Twitter"
                      >
                        <Twitter className="w-5 h-5" />
                      </a>
                    </div>
                    <div className="mt-4 text-sm text-muted-foreground space-y-1">
                      <p>Facebook: {companyInfo.contact.social.facebook}</p>
                      <p>LinkedIn: {companyInfo.contact.social.linkedin}</p>
                      <p>Twitter: {companyInfo.contact.social.twitter}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Placeholder */}
 <section className="bg-card">
  <div className="h-[400px] w-full overflow-hidden rounded-xl border border-border">
    <iframe
      title="Voitech Solutions Location Map"
      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4732.638733714472!2d32.61540577496454!3d0.3232212996736085!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x177dbbfac842eabf%3A0x2b14c6f7bfc00c19!2sVoitech%20solutions!5e1!3m2!1sen!2sug!4v1767263550783!5m2!1sen!2sug"
      className="h-full w-full"
      style={{ border: 0 }}
      allowFullScreen
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
    />
  </div>

  {/* Optional: keep address under the map */}
  <div className="container-custom py-6">
    <p className="text-center text-muted-foreground">
      Level 1, Maria House, Plot 12-13 Kataza Close, Bugolobi, Kampala
    </p>
  </div>
</section>
    </div>
  );
};

export default ContactPage;
